import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useGlobals } from 'src/shared/contexts/appContext';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import {
  getDistrictTitleByType,
  getDistrictTitleConfigByType,
  updateDistrictTitle,
  updateDistrictTitleConfig,
} from 'src/shared/services/districtService';
import { Button, Typography, Box } from '@mui/material';
import { Edit } from '@mui/icons-material';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import { MemberSimpleSearchModal } from 'src/modules/member/components/memberSimpleSearchModal';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { toast } from 'react-toastify';
import { OrganizationRoleTable } from 'src/shared/components/organisms/organizationRoleTable';
import { OrganizationTitleTypeEnum } from 'src/shared/models/enums/organizationTitleType.enum';
import { OrginizationResignConfirm } from 'src/shared/components/organisms/organizationResignConfirm';
import { getOrganizationDistrictId } from 'src/shared/functions/organizationHelpers';
import { OrganizationTitleConfigModal } from 'src/shared/components/organisms/organizationTitleConfigModal';
import { OrganizationTitleEditDateModal } from 'src/shared/components/organisms/organizationTitleEditDateModal';
import { DistrictTitleResponse } from '../models/districtTitleResponse';
import { DistrictTitleRequest } from '../models/districtTitleRequest';

type DistrictTitlePageProps = {
  titleType: OrganizationTitleTypeEnum;
};

export const DistrictTitlePage = (props: DistrictTitlePageProps) => {
  const { titleType } = props;
  const { t } = useTranslation(['common']);
  const [loading, setLoading] = useState<boolean>(false);
  const { dateStamp, setDateStamp } = useGlobals();

  const { currentUser } = useGlobals();
  const params = useParams();
  const currentDistrictId = getOrganizationDistrictId(currentUser, params.id);

  const [districtTitle, setDistrictTitle] = useState<DistrictTitleResponse[]>([]);
  const [selectedMemberTitleId, setSelectedMemberTitleId] = useState<number>();
  const [selectedMemberTitle, setSelectedMemberTitle] = useState<DistrictTitleResponse>();

  const [showUserModal, setShowUserModal] = useState<boolean>(false);
  const [showResignModal, setShowResignModal] = useState<boolean>(false);
  const [showTitleConfigModal, setShowTitleConfigModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  const isMounted = useIsMounted();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await getDistrictTitleByType(currentDistrictId, titleType);
        if (isMounted()) {
          setDistrictTitle(result.data);
          ConsoleHelper.log(dateStamp);
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      } finally {
        if (isMounted()) setLoading(false);
      }
    };

    fetchData();
  }, [isMounted, currentDistrictId, dateStamp, titleType]);

  const handleMemberSelect = async (memberId: number) => {
    setShowUserModal(false);
    const requestData: DistrictTitleRequest = {
      memberTitle_id: selectedMemberTitleId || 0,
      member_id: memberId,
      admDate: memberId > 0 ? DateHelper.getFSDate(new Date()) : '',
      resDate: null,
    };
    try {
      setLoading(true);
      await updateDistrictTitle(currentDistrictId, requestData);
      if (isMounted()) {
        toast.success(<>{t('common:statusMessages.saveChangesSuccess')}</>);
        setDateStamp(new Date());
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  const handleResign = () => {
    handleMemberSelect(0);
  };

  const refetchData = () => {
    setDateStamp(new Date());
  };

  return (
    <FormLoaderWrapper loading={loading}>
      <Box sx={{ p: 3, minHeight: '15rem', maxWidth: { md: '100%', lg: '100%', xl: '50%' } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant='h6' gutterBottom>
            {titleType === OrganizationTitleTypeEnum.Styrelse ? t('common:organization.boardTitle') : t('common:organization.electedTitle')}
          </Typography>
          <Box>
            <Button variant='outlined' size='small' startIcon={<Edit />} onClick={() => setShowTitleConfigModal(true)}>
              {titleType === OrganizationTitleTypeEnum.Styrelse ? t('common:organization.editBoard') : t('common:organization.editElected')}
            </Button>
          </Box>
        </Box>
        {districtTitle && (
          <OrganizationRoleTable<DistrictTitleResponse>
            roles={districtTitle}
            idColumn='memberTitle_id'
            titleColumn='title'
            valueColumn='name'
            resDateColumn='admDate'
            setUserCallback={(titleId) => {
              setSelectedMemberTitleId(titleId);
              setShowUserModal(true);
            }}
            editDateCallback={(titleId) => {
              const selectedTitle = districtTitle.find((x) => x.memberTitle_id === titleId);
              setSelectedMemberTitle(selectedTitle);
              setShowEditModal(true);
            }}
            deleteCallback={(titleId) => {
              setSelectedMemberTitleId(titleId);
              setShowResignModal(true);
            }}
          />
        )}
        {showTitleConfigModal && (
          <OrganizationTitleConfigModal
            modalOpen={showTitleConfigModal}
            onClose={() => setShowTitleConfigModal(false)}
            onSave={() => {
              setShowTitleConfigModal(false);
              refetchData();
            }}
            modalTitle={titleType === OrganizationTitleTypeEnum.Styrelse ? t('common:organization.editBoard') : t('common:organization.editElected')}
            type={titleType}
            organizationId={currentDistrictId}
            fetchMethod={async () => {
              return getDistrictTitleConfigByType(currentDistrictId, titleType);
            }}
            updateMethod={async (data) => {
              return updateDistrictTitleConfig(currentDistrictId, titleType, data);
            }}
          />
        )}
        <OrginizationResignConfirm
          modalOpen={showResignModal}
          onClose={() => setShowResignModal(false)}
          onConfirm={() => {
            setShowResignModal(false);
            handleResign();
          }}
        />
        <MemberSimpleSearchModal
          modalOpen={showUserModal}
          onClose={() => setShowUserModal(false)}
          onSelectMember={(memberId) => handleMemberSelect(memberId)}
        />
        {selectedMemberTitle && showEditModal && (
          <OrganizationTitleEditDateModal
            modalOpen={showEditModal}
            modalTitle={
              titleType === OrganizationTitleTypeEnum.Styrelse ? t('common:organization.editBoardUser') : t('common:organization.editElectedUser')
            }
            organizationTitleResponse={selectedMemberTitle}
            updateMethod={async (data) => {
              return updateDistrictTitle(currentDistrictId, data);
            }}
            onClose={() => setShowEditModal(false)}
            onSave={() => {
              setShowEditModal(false);
              refetchData();
            }}
          />
        )}
      </Box>
    </FormLoaderWrapper>
  );
};
